import { Controller } from "@hotwired/stimulus"

/**
 * Autofocus the lead_target input when the form swaps in via Turbo
 * Frame. On frame replace (save succeeds), restore focus to the
 * pencil edit button so keyboard users don't lose their place.
 *
 * v5 patch (FE-1) — DOM queries are SCOPED to the closest
 * `[data-controller~='live-dashboard']` ancestor so multi-EE views,
 * iframe-embedded surfaces, and any future page hosting >1 goal
 * surface don't fight over a single global `[aria-label='Edit goal']`
 * match. Falls back to `document` for the rare case where the
 * controller is mounted outside a live-dashboard root (defensive).
 */
export default class extends Controller {
  static targets = ["input"]

  connect() {
    if (!this.hasInputTarget) return
    // Defer one tick so Turbo has finished mounting the frame.
    window.requestAnimationFrame(() => this.inputTarget.focus({ preventScroll: false }))
  }

  disconnect() {
    // On frame replace we're torn down BEFORE the new markup mounts,
    // so query for the pencil edit button and queue focus on the
    // next frame. If the KPI strip rendered the prompt (no pencil),
    // this is a no-op.
    //
    // v5 patch (FE-1):
    //   1. Scope to the closest live-dashboard root so multi-EE pages
    //      don't tug focus to another card's pencil.
    //   2. Skip refocus entirely if a NEW goal-form-focus controller
    //      has already mounted (validation re-render path: server
    //      returned 422 + replaced the frame with another form, we
    //      do NOT want to yank focus away from the input the user is
    //      about to retry typing in).
    //   3. v5 SD coherence sweep landed on "Edit target" everywhere
    //      visible to the user (KPI label + aria-label + button text).
    //      The dual-probe selector is retained as a defensive
    //      belt-and-suspenders — if a future sprint reverts an
    //      individual partial to "Edit goal", focus restoration
    //      still works rather than silently no-op'ing. Drop the
    //      `'Edit goal'` half of the OR clause if "target" stays
    //      stable through Phase 4.
    window.requestAnimationFrame(() => {
      // (2) — a new form already grabbed the frame; let it own focus.
      if (document.querySelector("[data-goal-form-focus-target='input']")) return

      // (1) — scope query to the owning live-dashboard root.
      const root = this.element.closest("[data-controller~='live-dashboard']") || document
      // (3) — try both aria-label spellings.
      const pencil = root.querySelector("[aria-label='Edit target'], [aria-label='Edit goal']")
      if (pencil) pencil.focus({ preventScroll: true })
    })
  }
}
